<template>
  <div class="sort-question">
    <transition-group name="sort-move" tag="div" class="sort-question__list">
      <div
      v-for="(option, index) in displayOptions"
      :key="option._key"
      :data-key="option._key"
      class="sort-item"
      :class="{
        'is-disabled': disabled,
        'is-selected': option._selected,
        'is-dragging': draggingKey === option._key,
        'is-drag-over': dragOverKey === option._key
      }"
      :draggable="canDragItem(option)"
      @click="onItemClick(option)"
      @dragstart="onDragStart(option, $event)"
      @dragover="onDragOver(index, $event)"
      @drop="onDrop(index, $event)"
      @dragend="onDragEnd"
      @touchstart="onTouchStart(option, $event)"
      @touchmove="onTouchMove($event)"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div
        class="sort-item__rank"
        :class="getRankClass(option._key)"
      >
        {{ option._selected ? getSelectedRank(option._key) : '-' }}
      </div>
      <div class="sort-item__content">
        <el-image
          v-if="option.image"
          :src="option.image"
          fit="cover"
          class="sort-item__image"
        >
          <div slot="error" class="sort-item__image-fallback">
            {{ getOptionText(option).slice(0, 1) }}
          </div>
        </el-image>
        <div v-else class="sort-item__image-fallback">
          {{ getOptionText(option).slice(0, 1).toUpperCase() }}
        </div>
        <span class="sort-item__label">{{ getOptionText(option) }}</span>
      </div>
        <div class="sort-item__actions">
          <span class="sort-item__drag-icon" :class="{ 'is-enabled': canDragItem(option) }">
            <i class="el-icon-rank" />
          </span>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'SortQuestion',
  props: {
    value: {
      type: Array,
      default() {
        return []
      }
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayOptions: [],
      selectedKeys: [],
      draggingKey: null,
      dragOverKey: null,
      suppressSync: false,
      skipNextClick: false,
      skipClickTimer: null,
      touchStartPoint: null,
      touchMoved: false
    }
  },
  beforeDestroy() {
    if (this.skipClickTimer) {
      clearTimeout(this.skipClickTimer)
      this.skipClickTimer = null
    }
  },
  watch: {
    options: {
      handler() {
        this.rebuildFromProps()
      },
      immediate: true,
      deep: true
    },
    value: {
      handler() {
        if (!this.suppressSync) {
          this.rebuildSelectedFromValue()
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getOptionKey(option, index = 0) {
      if (option.uuid !== undefined && option.uuid !== null && option.uuid !== '') {
        return `uuid_${option.uuid}`
      }
      // value 可能因历史数据重复，拼上 index 保证 transition-group key 唯一
      if (option.value !== undefined && option.value !== null && option.value !== '') {
        return `value_${option.value}_${index}`
      }
      return `index_${index}`
    },
    getOptionText(option) {
      if (!option) return ''
      return option.label || option.name || ''
    },
    getSelectedRank(key) {
      const rank = this.selectedKeys.indexOf(key)
      if (rank < 0) return '-'
      return rank + 1
    },
    getRankClass(key) {
      const rank = this.selectedKeys.indexOf(key)
      if (rank < 0) return ''
      if (rank === 0) return 'rank-top-1'
      if (rank === 1) return 'rank-top-2'
      if (rank === 2) return 'rank-top-3'
      return 'rank-other'
    },
    canDragItem(option) {
      return !this.disabled && option._selected && this.selectedKeys.length > 1
    },
    rebuildFromProps() {
      const previousOrder = this.displayOptions.map((item) => item._key)
      const previousMap = {}
      this.displayOptions.forEach((item) => {
        previousMap[item._key] = item
      })

      const nextList = (this.options || []).map((item, index) => {
        const key = this.getOptionKey(item, index)
        const oldItem = previousMap[key]
        return {
          ...item,
          _key: key,
          _selected: oldItem ? oldItem._selected : false
        }
      })

      this.displayOptions = this.orderByPrevious(nextList, previousOrder)
      this.rebuildSelectedFromValue()
    },
    orderByPrevious(nextList, previousOrder) {
      if (!previousOrder || previousOrder.length === 0) {
        return nextList
      }
      const nextMap = {}
      nextList.forEach((item) => {
        nextMap[item._key] = item
      })
      const ordered = []
      previousOrder.forEach((key) => {
        if (nextMap[key]) {
          ordered.push(nextMap[key])
          delete nextMap[key]
        }
      })
      Object.keys(nextMap).forEach((key) => {
        ordered.push(nextMap[key])
      })
      return ordered
    },
    rebuildSelectedFromValue() {
      const nextSelectedKeys = []
      if (Array.isArray(this.value)) {
        this.value.forEach((label) => {
          const target = this.displayOptions.find((item) => this.getOptionText(item) === label)
          if (target && !nextSelectedKeys.includes(target._key)) {
            nextSelectedKeys.push(target._key)
          }
        })
      }
      this.selectedKeys = nextSelectedKeys
      this.displayOptions.forEach((item) => {
        item._selected = this.selectedKeys.includes(item._key)
      })
      this.reorderBySelected()
    },
    reorderBySelected() {
      const selected = []
      const unselected = []
      this.displayOptions.forEach((item) => {
        if (this.selectedKeys.includes(item._key)) {
          selected.push(item)
        } else {
          unselected.push(item)
        }
      })
      const sortedSelected = []
      this.selectedKeys.forEach((key) => {
        const target = selected.find((item) => item._key === key)
        if (target) {
          sortedSelected.push(target)
        }
      })
      this.displayOptions = [...sortedSelected, ...unselected]
    },
    onItemClick(option) {
      if (this.skipNextClick) {
        this.skipNextClick = false
        return
      }
      this.toggleSelect(option)
    },
    toggleSelect(option) {
      if (this.disabled) return
      const key = option._key
      const selectedIndex = this.selectedKeys.indexOf(key)
      if (selectedIndex > -1) {
        this.selectedKeys.splice(selectedIndex, 1)
      } else {
        this.selectedKeys.push(key)
      }
      this.displayOptions.forEach((item) => {
        item._selected = this.selectedKeys.includes(item._key)
      })
      this.reorderBySelected()
      this.emitValue()
    },
    reorderSelectedKeys(fromKey, toKey) {
      const fromIndex = this.selectedKeys.indexOf(fromKey)
      const toIndex = this.selectedKeys.indexOf(toKey)
      if (fromIndex === -1 || toIndex === -1) {
        return false
      }
      const selected = [...this.selectedKeys]
      const [moved] = selected.splice(fromIndex, 1)
      selected.splice(toIndex, 0, moved)
      this.selectedKeys = selected
      return true
    },
    onDragStart(option, event) {
      if (!this.canDragItem(option)) {
        event.preventDefault()
        return
      }
      this.draggingKey = option._key
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', option._key)
    },
    onDragOver(index, event) {
      const target = this.displayOptions[index]
      if (!target || !target._selected || !this.draggingKey || this.draggingKey === target._key) {
        return
      }
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      this.dragOverKey = target._key
    },
    onDrop(index, event) {
      const target = this.displayOptions[index]
      if (!target || !target._selected || !this.draggingKey || this.draggingKey === target._key) {
        this.onDragEnd()
        return
      }
      event.preventDefault()
      const hasReordered = this.reorderSelectedKeys(this.draggingKey, target._key)
      if (!hasReordered) {
        this.onDragEnd()
        return
      }
      this.reorderBySelected()
      this.emitValue()
      this.onDragEnd()
    },
    onTouchStart(option, event) {
      if (!this.canDragItem(option)) {
        return
      }
      const touch = event.touches && event.touches[0]
      if (!touch) {
        return
      }
      this.draggingKey = option._key
      this.dragOverKey = option._key
      this.touchStartPoint = {
        x: touch.clientX,
        y: touch.clientY
      }
      this.touchMoved = false
    },
    onTouchMove(event) {
      if (!this.draggingKey) {
        return
      }
      const touch = event.touches && event.touches[0]
      if (!touch) {
        return
      }
      if (this.touchStartPoint) {
        const deltaX = Math.abs(touch.clientX - this.touchStartPoint.x)
        const deltaY = Math.abs(touch.clientY - this.touchStartPoint.y)
        if (deltaX > 4 || deltaY > 4) {
          this.touchMoved = true
        }
      }
      event.preventDefault()
      const targetKey = this.getTouchTargetKey(touch.clientX, touch.clientY)
      if (!targetKey || targetKey === this.draggingKey) {
        return
      }
      const target = this.displayOptions.find((item) => item._key === targetKey)
      if (!target || !target._selected) {
        return
      }
      this.dragOverKey = targetKey
    },
    onTouchEnd() {
      if (!this.draggingKey) {
        this.onDragEnd()
        return
      }
      if (this.touchMoved) {
        this.scheduleSkipClick()
      }
      if (this.dragOverKey && this.dragOverKey !== this.draggingKey) {
        const hasReordered = this.reorderSelectedKeys(this.draggingKey, this.dragOverKey)
        if (hasReordered) {
          this.reorderBySelected()
          this.emitValue()
        }
      }
      this.onDragEnd()
      this.touchStartPoint = null
      this.touchMoved = false
    },
    getTouchTargetKey(clientX, clientY) {
      if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') {
        return null
      }
      const element = document.elementFromPoint(clientX, clientY)
      if (!element || !element.closest) {
        return null
      }
      const itemElement = element.closest('.sort-item')
      if (!itemElement || !itemElement.dataset) {
        return null
      }
      return itemElement.dataset.key || null
    },
    scheduleSkipClick() {
      this.skipNextClick = true
      if (this.skipClickTimer) {
        clearTimeout(this.skipClickTimer)
      }
      this.skipClickTimer = setTimeout(() => {
        this.skipNextClick = false
        this.skipClickTimer = null
      }, 250)
    },
    onDragEnd() {
      this.draggingKey = null
      this.dragOverKey = null
    },
    emitValue() {
      const result = this.selectedKeys
        .map((key) => this.displayOptions.find((item) => item._key === key))
        .filter((item) => item && item._selected)
        .map((item) => this.getOptionText(item))
      this.suppressSync = true
      this.$emit('update:value', result)
      this.$emit('change', result)
      this.$nextTick(() => {
        this.suppressSync = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sort-question {
  width: 100%;
}

.sort-question__list {
  position: relative;
}

.sort-move-move {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.sort-move-enter-active,
.sort-move-leave-active {
  transition: all 0.24s ease;
}

.sort-move-enter,
.sort-move-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

.sort-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 12px;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
}

.sort-item:hover {
  border-color: #d9e6ff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.sort-item.is-selected {
  border-color: #7da7ff;
  background: linear-gradient(180deg, #ffffff 0%, #f2f7ff 100%);
  box-shadow: 0 10px 28px rgba(35, 88, 220, 0.14);
}

.sort-item.is-dragging {
  opacity: 0.88;
  border-color: #4a81ff;
  box-shadow: 0 14px 30px rgba(35, 88, 220, 0.25);
}

.sort-item.is-drag-over {
  border-color: #3b82f6;
  border-style: dashed;
}

.sort-item.is-disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.sort-item__rank {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border: 1px solid #dce3ef;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.sort-item.is-selected .sort-item__rank {
  color: #1e293b;
  border-color: transparent;
}

.sort-item__rank.rank-top-1 {
  color: #7c2d12;
  background: linear-gradient(135deg, #fff1d6 0%, #ffd180 100%);
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.sort-item__rank.rank-top-2 {
  color: #334155;
  background: linear-gradient(135deg, #f8fafc 0%, #dbe3ef 100%);
  box-shadow: 0 4px 10px rgba(100, 116, 139, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.sort-item__rank.rank-top-3 {
  color: #78350f;
  background: linear-gradient(135deg, #ffe9dc 0%, #fdba74 100%);
  box-shadow: 0 4px 10px rgba(249, 115, 22, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.sort-item__rank.rank-other {
  color: #1d4ed8;
  background: linear-gradient(135deg, #e8f1ff 0%, #cfe2ff 100%);
  box-shadow: 0 3px 8px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.sort-item__content {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.sort-item__image,
.sort-item__image-fallback {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  flex-shrink: 0;
}

.sort-item__image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
}

.sort-item__label {
  margin-left: 10px;
  color: #111827;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sort-item__actions {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.sort-item__drag-icon {
  display: inline-flex;
  margin-left: 8px;
  color: #9aa4b2;
  font-size: 16px;
}

.sort-item__drag-icon.is-enabled {
  color: #4a81ff;
}
</style>
